import React, { useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import type { IViewProps } from 'common/types/views';
import type { WithChildren } from 'common/types';

import { FieldsType } from 'common/types/fields';
import { useGenericFieldsActionsAdopted, useGenericFieldsDataAdopted } from 'containers/FormDataAdapter';
import { determineAndApplyErrorToForm } from 'utils/determineAndApplyErrorToForm';

import FormWrapper from 'common/components/FormWrapper';
import ViewsButtonsBlock from 'common/components/ViewsButtonsBlock';
import { IFormValues } from 'common/components/FormHF/types';
import { prepareDefaultFormData } from 'common/components/FormHF/utils';
import FieldGenerator from 'common/components/FieldGenerator';
import { FormLoader } from 'common/components/Loader';
import Tooltip from 'common/components/Tooltip';


import { isFieldDisabled } from 'utils/isStatusDisabled';
import { useIsNextStepAvailable } from 'hooks/useIsNextStepAvailable';
import { DanflossProjectFields } from '@danfloss/containers/project-fields';
import classNames from 'classnames';
import styles from './styles.module.scss';
import FormWrapperStyles from '../FormWrapper.module.scss';
import AccessoriesRadioButtonsList from './components/AccessoriesRadioButtonsList';
import { useTranslateFn } from '../../../utils/useTranslateFn';

const AccessoriesSelectorView = ({
  onNextClickHandler,
  onBackClickHandler,
  onResetHandler,
  title,
  description,
  handleSetNoFieldsAvailable,
  fieldsNames,
  children,
  clearProgressError,
  isNextDisabled,
  isLoading: isCartLoading,
}: WithChildren<IViewProps<DanflossProjectFields>>) => {
  const t = useTranslateFn<string>();
  const idForm = 'form-accessories';
  const { error, isLoading, order } = useGenericFieldsDataAdopted<IFormValues<DanflossProjectFields>>();
  const { updateFieldsState, getFieldsByName } = useGenericFieldsActionsAdopted<IFormValues<DanflossProjectFields>>();

  const fields = useMemo(() => getFieldsByName(fieldsNames), [getFieldsByName, fieldsNames]);

  const defaultValues = prepareDefaultFormData(fields);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getFieldState,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
    formState: { errors },
    resetField,
    setError,
  } = useForm<IFormValues<DanflossProjectFields>>({ defaultValues });

  // eslint-disable-next-line no-unused-vars
  const onSubmitSuccess: SubmitHandler<IFormValues<DanflossProjectFields>> = useCallback((data) => {
    onNextClickHandler();
  }, [onNextClickHandler]);

  const handleFieldUpdate = useCallback((
    fieldType: FieldsType,
    fieldName: DanflossProjectFields,
    changedValue?: IFormValues<DanflossProjectFields>[DanflossProjectFields],
  ) => {
    if (clearProgressError) {
      clearProgressError();
    }
    updateFieldsState(fieldType, fieldName, changedValue);
  }, [updateFieldsState, clearProgressError]);

  useEffect(() => {
    if (error) {
      determineAndApplyErrorToForm(error, setError);
    }
  }, [error, setError]);

  useIsNextStepAvailable(order, fieldsNames, fields, handleSetNoFieldsAvailable);

  const accessoriesField = useMemo(() => (
    order && !!order.find((fieldName) => fieldName === DanflossProjectFields.accessories) && fields[DanflossProjectFields.accessories] && (
      <FieldGenerator<IFormValues<DanflossProjectFields>>
        className={styles.accessories}
        field={fields[DanflossProjectFields.accessories]!}
        name={fields[DanflossProjectFields.accessories]!.name as DanflossProjectFields}
        control={control}
        watch={watch}
        setValue={setValue}
        getFieldState={getFieldState}
        key={fields[DanflossProjectFields.accessories]!.name}
        onChangeHandler={handleFieldUpdate}
        isDisabled={isFieldDisabled(fields[DanflossProjectFields.accessories])}
        resetField={resetField}
        Component={{
          RadioButtonsList: AccessoriesRadioButtonsList,
        }}
      />
    )
  ), [control, fields, getFieldState, handleFieldUpdate, order, resetField, setValue, watch]);

  const testingField = useMemo(() => (
    order && !!order.find((fieldName) => fieldName === DanflossProjectFields.testingConfiguration) && fields[DanflossProjectFields.testingConfiguration] && (
      <div className={styles.testing}>
        <FieldGenerator<IFormValues<DanflossProjectFields>>
          className={styles.checkbox}
          field={fields[DanflossProjectFields.testingConfiguration]!}
          name={fields[DanflossProjectFields.testingConfiguration]!.name as DanflossProjectFields}
          control={control}
          watch={watch}
          setValue={setValue}
          getFieldState={getFieldState}
          key={fields[DanflossProjectFields.testingConfiguration]!.name}
          onChangeHandler={handleFieldUpdate}
          isDisabled={isFieldDisabled(fields[DanflossProjectFields.testingConfiguration])}
          resetField={resetField}
        />
      </div>
    )
  ), [control, fields, getFieldState, handleFieldUpdate, order, resetField, setValue, watch]);

  const customerField = useMemo(() => (
    order && !!order.find((fieldName) => fieldName === DanflossProjectFields.customerSerialNumber) && fields[DanflossProjectFields.customerSerialNumber] && (
      <div className={styles.partNumber}>
        <div className={classNames(styles.partNumberLabel, 'common-components-Input__label')}>
          Customer Part Number
          <Tooltip
            className={styles.iconTooltip}
            content="Optional field to enter a custom part number."
          />
        </div>
        <FieldGenerator<IFormValues<DanflossProjectFields>>
          className={styles.input}
          field={fields[DanflossProjectFields.customerSerialNumber]!}
          name={fields[DanflossProjectFields.customerSerialNumber]!.name as DanflossProjectFields}
          placeholder={t('texts.step5.customerPartNumberPlaceholder')}
          control={control}
          watch={watch}
          setValue={setValue}
          getFieldState={getFieldState}
          key={fields[DanflossProjectFields.customerSerialNumber]!.name}
          onChangeHandler={handleFieldUpdate}
          isDisabled={isFieldDisabled(fields[DanflossProjectFields.customerSerialNumber])}
          resetField={resetField}
        />
      </div>
    )
  ), [control, fields, getFieldState, handleFieldUpdate, order, resetField, setValue, watch]);

  return (
    <FormWrapper
      header={title}
      description={description}
      overrideStyles={FormWrapperStyles}
    >
      <form
        id={idForm}
        onSubmit={handleSubmit(onSubmitSuccess)}
      >
        {(isLoading || isCartLoading) && <FormLoader />}
        {accessoriesField}
        <div className={styles.box}>
          {testingField}
          {customerField}
        </div>
        {order && order.map((fieldName) => {
          if (fieldsNames.includes(fieldName as DanflossProjectFields) && !!fields[fieldName]) {
            switch (fieldName) {
              case DanflossProjectFields.accessories:
              case DanflossProjectFields.testingConfiguration:
              case DanflossProjectFields.customerSerialNumber:
                return null;
              default: {
                return (
                  <FieldGenerator<IFormValues<DanflossProjectFields>>
                    className={styles.input}
                    field={fields[fieldName]!}
                    name={fields[fieldName]!.name as DanflossProjectFields}
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    getFieldState={getFieldState}
                    key={fields[fieldName]!.name}
                    onChangeHandler={handleFieldUpdate}
                    isDisabled={isFieldDisabled(fields[fieldName])}
                    resetField={resetField}
                  />
                );
              }
            }
          }
          return null;
        })}
        {children}
      </form>
      <ViewsButtonsBlock
        idForm={idForm}
        onResetClickHandler={onResetHandler}
        onBackClickHandler={onBackClickHandler}
        isNextDisabled={isNextDisabled}
      />
    </FormWrapper>
  );
};

// @ts-ignore
export default AccessoriesSelectorView;
